<script setup lang="ts">
import { renderMarkdown } from "@/lib/markdown";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";

import { apiClient, ApiError } from "@/api/client";
import type { GradeResponse, QuestionResponse } from "@/api/types";
import PairingPanel from "@/components/PairingPanel.vue";
import ReportGradeModal from "@/components/ReportGradeModal.vue";
import ResponseFeedback from "@/components/feedback/ResponseFeedback.vue";
import MarkingPanel from "@/components/session/MarkingPanel.vue";
import RlCard from "@/components/ui/RlCard.vue";
import RlPill from "@/components/ui/RlPill.vue";

/**
 * Question mode: prompt + answer beside the marking panel (PE-8.3).
 *
 * The container owns session state, the tick and the focus blocks; this panel
 * owns one question at a time. It fetches every question *after* the first —
 * the first arrives as `firstQuestion` because the container generated it
 * behind the boot loader and the milestones have to come off that real await.
 *
 * `activity` feeds the container's on-device idle timer. It is throttled
 * rather than fired per keystroke: the container only needs to know the student
 * is alive, and a per-keystroke event would be a keylogger-shaped signal for no
 * gain.
 */

const props = defineProps<{
  subjectId: string;
  sessionId: string;
  topicId: string;
  firstQuestion: QuestionResponse | null;
}>();

const emit = defineEmits<{
  answered: [{ marks: number; available: number }];
  activity: [];
}>();

const question = ref<QuestionResponse | null>(null);
const answer = ref("");
const grade = ref<GradeResponse | null>(null);
const generating = ref(false);
const grading = ref(false);
const generateError = ref<string | null>(null);
const answerError = ref<string | null>(null);
const reportOpen = ref(false);
const answerBox = ref<HTMLTextAreaElement | null>(null);

// One photo per answer (PRD §6.7 hard cap). Cleared once consumed by a submit
// so the next answer starts text-only and the pairing panel resets to idle.
const attachmentId = ref<string | null>(null);
// Prior grades for the current question, oldest first. Rendered struck-through
// by MarkingPanel so a re-grade reads as an audit trail, not an overwrite.
const gradeHistory = ref<GradeResponse[]>([]);
const regradeCount = ref(0);
const regradeCap = ref<number | null>(null);
// Only a photographed answer can be re-photographed; a text-only answer has no
// image to re-take, so the CTA would be a dead end.
const submittedWithAttachment = ref(false);

watch(
  () => props.firstQuestion,
  (next) => {
    if (next !== null && question.value === null) question.value = next;
  },
  { immediate: true },
);

// Multi-step generation status. A static line reads as hung once latency clears
// 5s; naming what is actually happening also surfaces the novelty check
// (PRD §6.4 / §9.1) as a product property rather than dead time.
const LOADER_STEPS = [
  "Selecting a topic and difficulty…",
  "Drafting a fresh question in the style of past papers…",
  "Checking it isn’t a verbatim past question…",
] as const;
const STEP_MS = 3500;
const loaderStep = ref(0);
let loaderTimer: ReturnType<typeof setInterval> | null = null;

function startLoaderCycle(): void {
  loaderStep.value = 0;
  stopLoaderCycle();
  // The last step holds instead of wrapping: a loop that never lands is itself
  // a signal that something is broken.
  loaderTimer = setInterval(() => {
    if (loaderStep.value < LOADER_STEPS.length - 1) {
      loaderStep.value++;
    } else {
      stopLoaderCycle();
    }
  }, STEP_MS);
}

function stopLoaderCycle(): void {
  if (loaderTimer !== null) {
    clearInterval(loaderTimer);
    loaderTimer = null;
  }
}

onUnmounted(stopLoaderCycle);

const ACTIVITY_THROTTLE_MS = 5000;
let lastActivityAt = 0;

function signalActivity(force = false): void {
  const now = Date.now();
  if (!force && now - lastActivityAt < ACTIVITY_THROTTLE_MS) return;
  lastActivityAt = now;
  emit("activity");
}

const renderedQuestion = computed(() =>
  question.value === null ? "" : renderMarkdown(question.value.question_text),
);

const marksLabel = computed(() =>
  question.value === null ? "" : `${question.value.marks} marks`,
);

const questionTypeLabel = computed(() =>
  question.value === null ? "" : question.value.question_type.replace("_", " "),
);

const remainingLabel = computed(() => {
  const q = question.value;
  if (q === null) return "";
  return `Remaining today: ${q.daily_remaining} · this month: ${q.monthly_remaining}`;
});

const canSubmit = computed(
  () =>
    !grading.value &&
    (answer.value.trim().length > 0 || attachmentId.value !== null),
);

const regradeAtCap = computed(
  () => regradeCap.value !== null && regradeCount.value >= regradeCap.value,
);

function focusAnswer(): void {
  void nextTick(() => answerBox.value?.focus());
}

async function nextQuestion(): Promise<void> {
  if (generating.value) return;
  signalActivity(true);
  generating.value = true;
  generateError.value = null;
  answerError.value = null;
  answer.value = "";
  // Clear the previous turn before the request, not after it: leaving the old
  // question rendered under the loader for the 5-15s generation window reads as
  // if both states are live at once.
  question.value = null;
  grade.value = null;
  attachmentId.value = null;
  gradeHistory.value = [];
  regradeCount.value = 0;
  regradeCap.value = null;
  submittedWithAttachment.value = false;
  startLoaderCycle();
  try {
    question.value = await apiClient.generateGuidedQuestion(props.subjectId, {
      topic_id: props.topicId,
    });
    focusAnswer();
  } catch (e) {
    // A quota-exhausted 429 carries the server's own explanation of which limit
    // was hit and when it resets; a generic message would throw that away. That
    // route is outside §10's reason-code table, so its plain `detail` sentence
    // arrives in the same `code` slot a §10 code would.
    if (e instanceof ApiError && e.status === 429) {
      generateError.value =
        e.code ?? "Question limit reached. Try again later.";
    } else {
      generateError.value =
        e instanceof Error ? e.message : "Could not generate question.";
    }
  } finally {
    generating.value = false;
    stopLoaderCycle();
  }
}

async function submit(): Promise<void> {
  if (question.value === null || !canSubmit.value) return;
  signalActivity(true);
  grading.value = true;
  answerError.value = null;
  // Recorded before the attachment is cleared post-grade; the grade view reads
  // it to decide whether the re-photograph CTA means anything.
  submittedWithAttachment.value = attachmentId.value !== null;
  const attachments = attachmentId.value
    ? [{ kind: "image" as const, id: attachmentId.value }]
    : [];
  const isRegrade = gradeHistory.value.length > 0;
  try {
    if (isRegrade) {
      // Subsequent attempts on the same question go through the regrade
      // endpoint, which creates a fresh interaction pointing at the original
      // and enforces the per-course soft cap.
      const regrade = await apiClient.regradeAnswer(
        question.value.interaction_id,
        answer.value.trim(),
        attachments,
      );
      grade.value = regrade;
      regradeCount.value = regrade.regrade_count;
      regradeCap.value = regrade.regrade_cap;
      // No `answered` here. The container's reducer clamps marks at zero and
      // increments questionsAnswered on every event, so re-emitting would
      // count the same question twice and could never subtract a lost mark.
      // The first attempt's contribution to the goal therefore stands.
    } else {
      const graded = await apiClient.submitAnswer(
        question.value.interaction_id,
        answer.value.trim(),
        attachments,
      );
      grade.value = graded;
      emit("answered", {
        marks: graded.marks_awarded,
        available: graded.marks_available,
      });
    }
    attachmentId.value = null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 429) {
      answerError.value =
        e.code ??
        (isRegrade
          ? "Re-grade cap reached."
          : "Marking limit reached. Try again later.");
    } else {
      answerError.value =
        e instanceof Error ? e.message : "Could not grade your answer.";
    }
  } finally {
    grading.value = false;
  }
}

function startRegrade(): void {
  // Move the current grade into history and re-open the form. The typed answer
  // survives so it can be edited; the attachment is cleared so the pairing
  // panel resets to its idle camera state for the new photo.
  if (grade.value !== null) {
    gradeHistory.value = [...gradeHistory.value, grade.value];
    grade.value = null;
  }
  attachmentId.value = null;
  answerError.value = null;
  signalActivity(true);
  focusAnswer();
}

async function submitReport(reason: string | null): Promise<void> {
  if (question.value === null) return;
  try {
    await apiClient.reportGrade(question.value.interaction_id, reason);
    reportOpen.value = false;
  } catch (e) {
    console.error("Grade report failed", e);
  }
}
</script>

<template>
  <div class="question-mode">
    <RlCard class="question">
      <div class="bar">
        <p class="eyebrow">Question</p>
        <span v-if="question" class="marks">{{ marksLabel }}</span>
      </div>

      <div v-if="generating" class="generating" data-testid="generating-state">
        <ul class="loader-steps" aria-live="polite">
          <li
            v-for="(step, idx) in LOADER_STEPS"
            :key="step"
            :class="{
              'step-active': idx === loaderStep,
              'step-done': idx < loaderStep,
            }"
          >
            {{ step }}
          </li>
        </ul>
        <p class="hint">
          Generating a fresh question typically takes 5–15 seconds.
        </p>
      </div>

      <p v-else-if="generateError" class="error" role="alert">
        {{ generateError }}
      </p>

      <template v-else-if="question">
        <div class="prompt" v-html="renderedQuestion" />
        <div class="meta">
          <RlPill>{{ question.topic }}</RlPill>
          <RlPill tone="info">{{ questionTypeLabel }}</RlPill>
        </div>

        <template v-if="grade === null">
          <textarea
            ref="answerBox"
            v-model="answer"
            rows="8"
            placeholder="Write your answer here, or attach a photo of your working from your phone."
            @input="signalActivity()"
          />
          <div class="actions">
            <PairingPanel
              :session-id="sessionId"
              :attachment-id="attachmentId"
              @attached="(id: string) => (attachmentId = id)"
              @cleared="attachmentId = null"
            />
            <button class="primary" :disabled="!canSubmit" @click="submit">
              {{ grading ? "Marking…" : "Mark my answer" }}
            </button>
          </div>
          <p v-if="answerError" class="error" role="alert">{{ answerError }}</p>
        </template>

        <div v-else class="actions">
          <button class="primary" @click="nextQuestion">Next question</button>
        </div>

        <p class="remaining">{{ remainingLabel }}</p>
      </template>
    </RlCard>

    <MarkingPanel :grade="grade" :marking="grading" :history="gradeHistory">
      <template v-if="grade">
        <ResponseFeedback
          :key="grade.interaction_id"
          :interaction-id="grade.interaction_id"
        />
        <div class="grade-actions">
          <button class="secondary" @click="reportOpen = true">
            Report this grade
          </button>
          <button
            v-if="submittedWithAttachment && !regradeAtCap"
            class="secondary"
            data-testid="practice-regrade-trigger"
            @click="startRegrade"
          >
            Re-photograph and re-grade
          </button>
          <p
            v-else-if="submittedWithAttachment && regradeAtCap"
            class="cap-nudge"
            data-testid="practice-regrade-cap-nudge"
          >
            Re-photograph limit reached. Try asking the tutor a follow-up
            question instead.
          </p>
        </div>
      </template>
    </MarkingPanel>

    <ReportGradeModal
      v-if="reportOpen"
      @submit="submitReport"
      @cancel="reportOpen = false"
    />
  </div>
</template>

<style scoped>
.question-mode {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-4);
  align-items: start;
}

@media (max-width: 900px) {
  .question-mode {
    grid-template-columns: 1fr;
  }
}

.bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp-3);
}

.eyebrow {
  margin: 0;
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--rl-fg-3);
}

.marks {
  font-family: var(--rl-font-mono);
  font-size: var(--rl-text-xs);
  color: var(--rl-fg-3);
}

.prompt {
  font-size: var(--rl-text-lg);
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: -0.01em;
}

.prompt :deep(p) {
  margin: 0 0 var(--sp-3);
}

.prompt :deep(table) {
  width: 100%;
  margin: var(--sp-3) 0;
  font-size: var(--rl-text-base);
  font-weight: 400;
  border-collapse: collapse;
}

.prompt :deep(th),
.prompt :deep(td) {
  padding: var(--sp-1) var(--sp-2);
  text-align: left;
  border: 1px solid var(--rl-border-subtle);
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
}

textarea {
  width: 100%;
  min-height: 130px;
  resize: vertical;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  margin-top: var(--sp-3);
}

.generating {
  min-height: 210px;
}

.loader-steps {
  margin: 0 0 var(--sp-3);
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.loader-steps li {
  font-size: var(--rl-text-base);
  color: var(--rl-fg-4);
  transition: color var(--t-base) var(--ease-standard);
}

.loader-steps .step-active {
  color: var(--rl-fg-1);
  font-weight: 500;
}

.loader-steps .step-done {
  color: var(--rl-fg-3);
}

.hint,
.remaining {
  margin: var(--sp-3) 0 0;
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
}

.grade-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-4);
}

.cap-nudge {
  margin: 0;
  font-size: var(--rl-text-sm);
  color: var(--rl-fg-3);
  line-height: 1.45;
}

.error {
  margin: var(--sp-3) 0 0;
}
</style>
